import React, { useState, useEffect } from 'react'
import { 
  ArrowLeftRight, 
  Building2, 
  CheckCircle2, 
  Clock, 
  AlertTriangle,
  DollarSign,
  TrendingUp,
  TrendingDown,
  FileText,
  Send,
  Download,
  RefreshCw,
  Info,
  CreditCard,
  Smartphone,
  Building,
  ChevronDown,
  ChevronUp,
  X,
  Check
} from 'lucide-react'
import { useAdminAuth } from '../../contexts/AdminAuthContext'
import { apiService } from '../../services/apiService'

const SettlementDashboard = () => {
  const { admin } = useAdminAuth()
  const [loading, setLoading] = useState(true)
  const [mgPosition, setMgPosition] = useState(null)
  const [floatLimit, setFloatLimit] = useState(null)
  const [settlementBatches, setSettlementBatches] = useState([])
  const [ufccCommission, setUfccCommission] = useState({ pending: 0, paid: 0 })
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [selectedBatch, setSelectedBatch] = useState(null)
  const [confirmForm, setConfirmForm] = useState({
    bank_reference: '',
    mobile_money_reference: '',
    payment_method: 'bank'
  })
  const [showUfccSettleModal, setShowUfccSettleModal] = useState(false)
  const [ufccSettleForm, setUfccSettleForm] = useState({
    amount: 0,
    bank_reference: '',
    mobile_money_reference: '',
    payment_method: 'bank'
  })
  const [expandedBatch, setExpandedBatch] = useState(null)

  // Get MG key from admin context - API returns mgCode
  const mgKey = admin?.mgCode || admin?.mgKey || admin?.mg_key || admin?.tenantKey || 'demo'

  useEffect(() => {
    fetchSettlementData()
  }, [mgKey])

  const fetchSettlementData = async () => {
    try {
      setLoading(true)
      
      const [positionRes, floatRes, batchesRes] = await Promise.all([
        apiService.getKataleMgPosition(mgKey),
        apiService.checkKataleFloatLimit(mgKey),
        apiService.getKataleSettlementBatches(mgKey)
      ])

      if (positionRes.status === 'SUCCESS') {
        setMgPosition(positionRes.data)
      }

      if (floatRes.status === 'SUCCESS') {
        setFloatLimit(floatRes.data)
      }

      if (batchesRes.status === 'SUCCESS') {
        setSettlementBatches(batchesRes.data || [])
      }

      // Fetch UFCC commission data
      await fetchUfccCommission()

    } catch (error) {
      console.error('Failed to fetch settlement data:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchUfccCommission = async () => {
    try {
      const response = await apiService.makeAuthenticatedRequest('KATALE-GET-UFCC-COMMISSION', {
        mg_key: mgKey
      }, true)
      
      if (response.status === 'SUCCESS') {
        setUfccCommission(response.data)
      }
    } catch (error) {
      console.error('Failed to fetch UFCC commission:', error)
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-UG', {
      style: 'currency',
      currency: 'UGX',
      minimumFractionDigits: 0
    }).format(amount || 0)
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('en-UG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const handleConfirmSettlement = async () => {
    if (!selectedBatch) return

    const reference = confirmForm.payment_method === 'bank' 
      ? confirmForm.bank_reference 
      : confirmForm.mobile_money_reference

    if (!reference) {
      alert('Please enter a payment reference')
      return
    }

    try {
      const response = await apiService.confirmKataleSettlement(
        selectedBatch.id,
        mgKey,
        confirmForm.payment_method === 'bank' ? confirmForm.bank_reference : null,
        confirmForm.payment_method === 'mobile_money' ? confirmForm.mobile_money_reference : null
      )

      if (response.status === 'SUCCESS') {
        alert(response.message)
        setShowConfirmModal(false)
        setSelectedBatch(null)
        setConfirmForm({ bank_reference: '', mobile_money_reference: '', payment_method: 'bank' })
        fetchSettlementData()
      } else {
        alert('Failed to confirm settlement: ' + response.message)
      }
    } catch (error) {
      alert('Error confirming settlement: ' + error.message)
    }
  }

  const handleSettleUfcc = async () => {
    const reference = ufccSettleForm.payment_method === 'bank' 
      ? ufccSettleForm.bank_reference 
      : ufccSettleForm.mobile_money_reference

    if (!reference) {
      alert('Please enter a payment reference')
      return
    }

    if (!ufccSettleForm.amount || ufccSettleForm.amount <= 0) {
      alert('Please enter a valid amount')
      return
    }

    try {
      const response = await apiService.makeAuthenticatedRequest('KATALE-SETTLE-UFCC-COMMISSION', {
        mg_key: mgKey,
        amount: ufccSettleForm.amount,
        bank_reference: ufccSettleForm.payment_method === 'bank' ? ufccSettleForm.bank_reference : null,
        mobile_money_reference: ufccSettleForm.payment_method === 'mobile_money' ? ufccSettleForm.mobile_money_reference : null
      }, true)

      if (response.status === 'SUCCESS') {
        alert('UFCC commission settled successfully!')
        setShowUfccSettleModal(false)
        setUfccSettleForm({ amount: 0, bank_reference: '', mobile_money_reference: '', payment_method: 'bank' })
        fetchUfccCommission()
      } else {
        alert('Failed to settle UFCC commission: ' + response.message)
      }
    } catch (error) {
      alert('Error settling UFCC commission: ' + error.message)
    }
  }

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      partial: 'bg-blue-100 text-blue-800',
      settled: 'bg-green-100 text-green-800',
      disputed: 'bg-red-100 text-red-800'
    }
    
    const icons = {
      pending: <Clock className="w-3 h-3" />,
      partial: <ArrowLeftRight className="w-3 h-3" />,
      settled: <CheckCircle2 className="w-3 h-3" />,
      disputed: <AlertTriangle className="w-3 h-3" />
    }

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {icons[status]}
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">MG Katale Settlement</h1>
          <p className="text-gray-600">Manage cross-MG transactions and UFCC commission payments</p>
        </div>
        <button
          onClick={fetchSettlementData}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Net Position Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-gray-500 text-sm font-medium">Net Position</span>
            {mgPosition?.net_position >= 0 ? (
              <TrendingUp className="w-5 h-5 text-green-500" />
            ) : (
              <TrendingDown className="w-5 h-5 text-red-500" />
            )}
          </div>
          <p className={`text-2xl font-bold ${mgPosition?.net_position >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatCurrency(Math.abs(mgPosition?.net_position || 0))}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {mgPosition?.net_position >= 0 ? 'Net receivable from other MGs' : 'Net payable to other MGs'}
          </p>
        </div>

        {/* You Owe Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-gray-500 text-sm font-medium">You Owe</span>
            <TrendingDown className="w-5 h-5 text-red-500" />
          </div>
          <p className="text-2xl font-bold text-red-600">
            {formatCurrency(mgPosition?.total_owed_to_others || 0)}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            To {mgPosition?.owes_to?.length || 0} MG(s)
          </p>
        </div>

        {/* Owed to You Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-gray-500 text-sm font-medium">Owed to You</span>
            <TrendingUp className="w-5 h-5 text-green-500" />
          </div>
          <p className="text-2xl font-bold text-green-600">
            {formatCurrency(mgPosition?.total_receivable_from_others || 0)}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            From {mgPosition?.owed_by?.length || 0} MG(s)
          </p>
        </div>

        {/* Float Limit Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-gray-500 text-sm font-medium">Float Available</span>
            <DollarSign className="w-5 h-5 text-blue-500" />
          </div>
          <p className="text-2xl font-bold text-blue-600">
            {formatCurrency(floatLimit?.available_float || 0)}
          </p>
          <div className="mt-2">
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>Used: {formatCurrency(floatLimit?.current_exposure || 0)}</span>
              <span>Limit: {formatCurrency(floatLimit?.float_limit || 5000000)}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full ${
                  ((floatLimit?.current_exposure || 0) / (floatLimit?.float_limit || 5000000)) > 0.8 
                    ? 'bg-red-500' 
                    : 'bg-blue-500'
                }`}
                style={{ 
                  width: `${Math.min(100, ((floatLimit?.current_exposure || 0) / (floatLimit?.float_limit || 5000000)) * 100)}%` 
                }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* UFCC Commission Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl shadow-lg p-6 mb-6 text-white">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-6 h-6" />
              <h2 className="text-xl font-bold">UFCC Commission Account</h2>
            </div>
            <p className="text-blue-100 text-sm">5% commission on all MG Katale transactions goes to UFCC</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
              <p className="text-blue-100 text-xs uppercase tracking-wide">Pending Commission</p>
              <p className="text-2xl font-bold">{formatCurrency(ufccCommission.pending || 0)}</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
              <p className="text-blue-100 text-xs uppercase tracking-wide">Total Paid</p>
              <p className="text-2xl font-bold">{formatCurrency(ufccCommission.paid || 0)}</p>
            </div>
          </div>
        </div>
        
        {ufccCommission.pending > 0 && (
          <div className="mt-4 pt-4 border-t border-white/20">
            <button
              onClick={() => {
                setUfccSettleForm({ ...ufccSettleForm, amount: ufccCommission.pending })
                setShowUfccSettleModal(true)
              }}
              className="flex items-center gap-2 px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 font-medium"
            >
              <Send className="w-4 h-4" />
              Settle UFCC Commission
            </button>
            <p className="text-blue-100 text-xs mt-2">
              <Info className="w-3 h-3 inline mr-1" />
              Pay via bank transfer or mobile money to UFCC account
            </p>
          </div>
        )}
      </div>

      {/* Position Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* What You Owe */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 bg-red-50">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <TrendingDown className="w-5 h-5 text-red-500" />
              Amounts You Owe to Other MGs
            </h3>
          </div>
          <div className="p-5">
            {mgPosition?.owes_to?.length > 0 ? (
              <div className="space-y-3">
                {mgPosition.owes_to.map((item, index) => (
                  <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                        <Building className="w-5 h-5 text-red-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{item.counterparty_mg_code}</p>
                        <p className="text-xs text-gray-500">Pending settlement</p>
                      </div>
                    </div>
                    <p className="font-bold text-red-600">{formatCurrency(item.amount_owed)}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-green-500" />
                <p>No pending amounts owed</p>
              </div>
            )}
          </div>
        </div>

        {/* What's Owed to You */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 bg-green-50">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-500" />
              Amounts Owed to You by Other MGs
            </h3>
          </div>
          <div className="p-5">
            {mgPosition?.owed_by?.length > 0 ? (
              <div className="space-y-3">
                {mgPosition.owed_by.map((item, index) => (
                  <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <Building className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{item.counterparty_mg_code}</p>
                        <p className="text-xs text-gray-500">Awaiting their payment</p>
                      </div>
                    </div>
                    <p className="font-bold text-green-600">{formatCurrency(item.amount_owed_to_us)}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Info className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                <p>No pending receivables</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Settlement Batches */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-500" />
            Settlement Batches
          </h3>
          <p className="text-sm text-gray-500 mt-1">Weekly settlement records for cross-MG transactions</p>
        </div>
        
        <div className="overflow-x-auto">
          {settlementBatches.length > 0 ? (
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Batch ID</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">From → To</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Period</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Confirmations</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {settlementBatches.map((batch) => {
                  const isFromMg = batch.from_mg_code === mgKey
                  const isToMg = batch.to_mg_code === mgKey
                  const canConfirm = (isFromMg && !batch.confirmed_by_from_mg) || (isToMg && !batch.confirmed_by_to_mg)
                  
                  return (
                    <React.Fragment key={batch.id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-5 py-4 whitespace-nowrap">
                          <button
                            onClick={() => setExpandedBatch(expandedBatch === batch.id ? null : batch.id)}
                            className="flex items-center gap-2 text-blue-600 font-medium"
                          >
                            {expandedBatch === batch.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            #{batch.id}
                          </button>
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span className={`font-medium ${isFromMg ? 'text-blue-600' : ''}`}>{batch.from_mg_code}</span>
                            <ArrowLeftRight className="w-4 h-4 text-gray-400" />
                            <span className={`font-medium ${isToMg ? 'text-blue-600' : ''}`}>{batch.to_mg_code}</span>
                          </div>
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap font-bold text-gray-900">
                          {formatCurrency(batch.net_amount)}
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(batch.settlement_period_start)} - {formatDate(batch.settlement_period_end)}
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap">
                          {getStatusBadge(batch.status)}
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${batch.confirmed_by_from_mg ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                              {batch.confirmed_by_from_mg ? <Check className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                              From
                            </span>
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${batch.confirmed_by_to_mg ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                              {batch.confirmed_by_to_mg ? <Check className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                              To
                            </span>
                          </div>
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap">
                          {canConfirm && batch.status !== 'settled' && (
                            <button
                              onClick={() => {
                                setSelectedBatch(batch)
                                setShowConfirmModal(true)
                              }}
                              className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                            >
                              <CheckCircle2 className="w-4 h-4" />
                              Confirm
                            </button>
                          )}
                          {batch.status === 'settled' && (
                            <span className="text-green-600 text-sm flex items-center gap-1">
                              <CheckCircle2 className="w-4 h-4" />
                              Settled
                            </span>
                          )}
                        </td>
                      </tr>
                      
                      {/* Expanded Details */}
                      {expandedBatch === batch.id && (
                        <tr>
                          <td colSpan={7} className="px-5 py-4 bg-gray-50">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Bank Reference</p>
                                <p className="font-medium">{batch.bank_reference || '-'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Mobile Money Reference</p>
                                <p className="font-medium">{batch.mobile_money_reference || '-'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Settled At</p>
                                <p className="font-medium">{formatDate(batch.settled_at)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">From MG Confirmed</p>
                                <p className="font-medium">{batch.confirmed_by_from_mg ? formatDate(batch.confirmed_by_from_mg_at) : 'Pending'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">To MG Confirmed</p>
                                <p className="font-medium">{batch.confirmed_by_to_mg ? formatDate(batch.confirmed_by_to_mg_at) : 'Pending'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Transaction Count</p>
                                <p className="font-medium">{batch.transaction_count || 0} transactions</p>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p className="font-medium">No settlement batches yet</p>
              <p className="text-sm">Settlement batches are created weekly for cross-MG transactions</p>
            </div>
          )}
        </div>
      </div>

      {/* How to Settle Section */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h3 className="font-semibold text-blue-900 flex items-center gap-2 mb-4">
          <Info className="w-5 h-5" />
          How Settlement Works
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-4">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mb-3">
              <span className="text-blue-600 font-bold">1</span>
            </div>
            <h4 className="font-medium text-gray-900 mb-1">Weekly Batches</h4>
            <p className="text-sm text-gray-600">Cross-MG transactions are grouped into weekly settlement batches</p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mb-3">
              <span className="text-blue-600 font-bold">2</span>
            </div>
            <h4 className="font-medium text-gray-900 mb-1">Make Payment</h4>
            <p className="text-sm text-gray-600">Transfer funds via bank or mobile money to the creditor MG</p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mb-3">
              <span className="text-blue-600 font-bold">3</span>
            </div>
            <h4 className="font-medium text-gray-900 mb-1">Record Reference</h4>
            <p className="text-sm text-gray-600">Enter the bank/mobile money reference number for tracking</p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mb-3">
              <span className="text-blue-600 font-bold">4</span>
            </div>
            <h4 className="font-medium text-gray-900 mb-1">Both Confirm</h4>
            <p className="text-sm text-gray-600">Both parties must confirm the settlement to complete it</p>
          </div>
        </div>
        
        <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">
            <AlertTriangle className="w-4 h-4 inline mr-1" />
            <strong>Important:</strong> Don't forget to settle UFCC commission! 5% of all transactions must be paid to the UFCC central account.
          </p>
        </div>
      </div>

      {/* Confirm Settlement Modal */}
      {showConfirmModal && selectedBatch && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Confirm Settlement</h3>
              <button onClick={() => setShowConfirmModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Settlement Batch #{selectedBatch.id}</p>
              <p className="font-bold text-lg">{formatCurrency(selectedBatch.net_amount)}</p>
              <p className="text-sm text-gray-500">
                {selectedBatch.from_mg_code} → {selectedBatch.to_mg_code}
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setConfirmForm({ ...confirmForm, payment_method: 'bank' })}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border ${
                    confirmForm.payment_method === 'bank' 
                      ? 'border-blue-600 bg-blue-50 text-blue-600' 
                      : 'border-gray-200 text-gray-600'
                  }`}
                >
                  <CreditCard className="w-4 h-4" />
                  Bank
                </button>
                <button
                  onClick={() => setConfirmForm({ ...confirmForm, payment_method: 'mobile_money' })}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border ${
                    confirmForm.payment_method === 'mobile_money' 
                      ? 'border-blue-600 bg-blue-50 text-blue-600' 
                      : 'border-gray-200 text-gray-600'
                  }`}
                >
                  <Smartphone className="w-4 h-4" />
                  Mobile Money
                </button>
              </div>
            </div>

            {confirmForm.payment_method === 'bank' ? (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Bank Reference Number</label>
                <input
                  type="text"
                  value={confirmForm.bank_reference}
                  onChange={(e) => setConfirmForm({ ...confirmForm, bank_reference: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., TRX123456789"
                />
              </div>
            ) : (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Mobile Money Reference</label>
                <input
                  type="text"
                  value={confirmForm.mobile_money_reference}
                  onChange={(e) => setConfirmForm({ ...confirmForm, mobile_money_reference: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., MM123456789"
                />
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSettlement}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Confirm Settlement
              </button>
            </div>
          </div>
        </div>
      )}

      {/* UFCC Settlement Modal */}
      {showUfccSettleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Settle UFCC Commission</h3>
              <button onClick={() => setShowUfccSettleModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-600">UFCC Commission Payment</p>
              <p className="font-bold text-lg">{formatCurrency(ufccCommission.pending)}</p>
              <p className="text-sm text-gray-500">5% commission from MG Katale transactions</p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Amount to Settle</label>
              <input
                type="number"
                value={ufccSettleForm.amount}
                onChange={(e) => setUfccSettleForm({ ...ufccSettleForm, amount: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter amount"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setUfccSettleForm({ ...ufccSettleForm, payment_method: 'bank' })}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border ${
                    ufccSettleForm.payment_method === 'bank' 
                      ? 'border-blue-600 bg-blue-50 text-blue-600' 
                      : 'border-gray-200 text-gray-600'
                  }`}
                >
                  <CreditCard className="w-4 h-4" />
                  Bank
                </button>
                <button
                  onClick={() => setUfccSettleForm({ ...ufccSettleForm, payment_method: 'mobile_money' })}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border ${
                    ufccSettleForm.payment_method === 'mobile_money' 
                      ? 'border-blue-600 bg-blue-50 text-blue-600' 
                      : 'border-gray-200 text-gray-600'
                  }`}
                >
                  <Smartphone className="w-4 h-4" />
                  Mobile Money
                </button>
              </div>
            </div>

            {ufccSettleForm.payment_method === 'bank' ? (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Bank Reference Number</label>
                <input
                  type="text"
                  value={ufccSettleForm.bank_reference}
                  onChange={(e) => setUfccSettleForm({ ...ufccSettleForm, bank_reference: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., TRX123456789"
                />
              </div>
            ) : (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Mobile Money Reference</label>
                <input
                  type="text"
                  value={ufccSettleForm.mobile_money_reference}
                  onChange={(e) => setUfccSettleForm({ ...ufccSettleForm, mobile_money_reference: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., MM123456789"
                />
              </div>
            )}

            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-xs text-yellow-800">
                <Info className="w-3 h-3 inline mr-1" />
                UFCC Bank Account: <strong>Bank of Uganda - 0123456789</strong><br />
                Mobile Money: <strong>0700 123 456</strong>
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowUfccSettleModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSettleUfcc}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Confirm Payment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SettlementDashboard
